import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FeedbackCategory } from '../proto/generated/user-feedback'

export const MAX_ATTACHMENTS = 5

export type FeedbackMediaAttachment = {
    type: 'screenshot' | 'video'
    url: string
}

export interface FeedbackStateType {
    isExpanded: boolean
    isClosing: boolean
    panelEntered: boolean
    category: FeedbackCategory
    description: string
    attachments: FeedbackMediaAttachment[]
    isCapturing: boolean
    captureError: string | null
}

const initialState: FeedbackStateType = {
    isExpanded: false,
    isClosing: false,
    panelEntered: false,
    category: FeedbackCategory.FEEDBACK,
    description: '',
    attachments: [],
    isCapturing: false,
    captureError: null
}

function revokeUrl(url: string) {
    try { URL.revokeObjectURL(url) } catch { /* ignore */ }
}

const feedbackSlice = createSlice({
    name: 'feedback',
    initialState,
    reducers: {
        openFeedbackPanel(state) {
            state.isExpanded = true
            state.isClosing = false
            state.panelEntered = false
        },
        closeFeedbackPanel(state) {
            state.isClosing = true
        },
        setPanelEntered(state, action: PayloadAction<boolean>) {
            state.panelEntered = action.payload
        },
        resetFeedbackPanel(state) {
            state.isExpanded = false
            state.isClosing = false
            state.panelEntered = false
            state.category = FeedbackCategory.FEEDBACK
            state.description = ''
            state.attachments.forEach((a) => revokeUrl(a.url))
            state.attachments = []
            state.captureError = null
        },
        setCategory(state, action: PayloadAction<FeedbackCategory>) {
            state.category = action.payload
        },
        setDescription(state, action: PayloadAction<string>) {
            state.description = action.payload
        },
        addAttachment(state, action: PayloadAction<FeedbackMediaAttachment>) {
            if (state.attachments.length < MAX_ATTACHMENTS) {
                state.attachments.push(action.payload)
            }
        },
        removeAttachment(state, action: PayloadAction<number>) {
            const [removed] = state.attachments.splice(action.payload, 1)
            if (removed) revokeUrl(removed.url)
        },
        setCapturing(state, action: PayloadAction<boolean>) {
            state.isCapturing = action.payload
        },
        setCaptureError(state, action: PayloadAction<string | null>) {
            state.captureError = action.payload
        },
        collapsePanelForCapture(state) {
            state.isExpanded = false
        },
        expandPanelAfterCapture(state) {
            state.isExpanded = true
        }
    }
})

export const {
    openFeedbackPanel,
    closeFeedbackPanel,
    setPanelEntered,
    resetFeedbackPanel,
    setCategory,
    setDescription,
    addAttachment,
    removeAttachment,
    setCapturing,
    setCaptureError,
    collapsePanelForCapture,
    expandPanelAfterCapture
} = feedbackSlice.actions

export default feedbackSlice.reducer
